import React, { ReactNode, useCallback, useMemo, useRef, useState } from 'react';
import {
  Select as SelectComponent,
  MenuItem,
  FormControl,
  InputLabel,
  SelectProps,
  FormHelperText,
  useTheme,
  Typography,
  Box,
  Chip,
  Checkbox,
  Tooltip,
  useMediaQuery,
  ListSubheader
} from '@mui/material';
import { Controller, Control } from 'react-hook-form';
import Search from 'components/Common/Search';
import { FaRegCirclePlay } from 'react-icons/fa6';
import { MdStopCircle } from 'react-icons/md';
import { BiPlus } from 'react-icons/bi';
import { RxCross2 } from 'react-icons/rx';
import { IoChevronDownSharp } from 'react-icons/io5';

export interface Option {
  value: string | number;
  label: string | ReactNode;
  disabled?: boolean;
  selected?: boolean;
  audioUrl?: string;
  category?: string;
}

const isOptionValueEqual = (a: string | number, b: string | number) => String(a) === String(b);

const SELECT_ALL_VALUE = '__SELECT_ALL__';

interface Props extends Omit<SelectProps, 'name' | 'label'> {
  name: string;
  control: Control<any>;
  label: string;
  helperText?: ReactNode;
  isLoading?: boolean;
  options: Option[];
  onScrollEnd?: () => void;
  handleAddNewOption?: () => void;
  sortOrder?: 'asc' | 'desc' | 'none';
  rules?: any;
  selectionLimit?: number;
  immutableOptions?: (string | number)[];
  searchable?: boolean;
  searchPlaceholder?: string;
  visibleCount?: number;
  /** When true with `multiple`, shows an "All" option at the top of the list. */
  allSelect?: boolean;
}

const getOptionSearchText = (option: Option) =>
  typeof option.label === 'string' ? option.label : String(option.value);

export default function Select({
  name,
  control,
  label,
  helperText,
  isLoading,
  options,
  onScrollEnd,
  handleAddNewOption,
  rules,
  sortOrder = 'asc',
  selectionLimit,
  immutableOptions = [],
  searchable = false,
  visibleCount = 3,
  searchPlaceholder = 'Search',
  allSelect = false,
  onChange: customOnChange,
  ...selectProps
}: Props) {
  const isMultiple = Boolean(selectProps.multiple);
  const immutableValues = immutableOptions ?? [];
  const theme = useTheme();
  const isLargeScreen = useMediaQuery(theme.breakpoints.up('lg'));
  const size = selectProps.size || (isLargeScreen ? 'medium' : 'small');
  const audioRefs = useRef<{ [key: string]: HTMLAudioElement | null }>({});
  const formControlRef = useRef<HTMLDivElement>(null);
  const searchWrapperRef = useRef<HTMLDivElement>(null);
  const [playingAudio, setPlayingAudio] = useState<string | number | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [menuOpen, setMenuOpen] = useState(false);
  const [menuMinWidth, setMenuMinWidth] = useState<number>();

  const handleScroll = useCallback(
    (event: React.UIEvent<HTMLElement>) => {
      if (onScrollEnd) {
        const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;
        // Use a tolerance — at non-100% zoom, scroll metrics are fractional and
        // strict equality never matches, so infinite scroll never fires.
        const reachedBottom = scrollHeight - scrollTop - clientHeight <= 2;
        if (reachedBottom) onScrollEnd();
      }
    },
    [onScrollEnd]
  );

  const sortedOptions = useMemo(() => {
    if (sortOrder === 'none') return options;
    const sorted = [...options].sort((a, b) => {
      const aVal = typeof a.label === 'string' ? a.label : String(a.value);
      const bVal = typeof b.label === 'string' ? b.label : String(b.value);
      return aVal.localeCompare(bVal, undefined, {
        numeric: true,
        sensitivity: 'base'
      });
    });

    return sortOrder === 'desc' ? sorted.reverse() : sorted;
  }, [options, sortOrder]);

  const filteredOptions = useMemo(() => {
    if (!searchable || !searchQuery.trim()) return sortedOptions;
    const query = searchQuery.trim().toLowerCase();
    return sortedOptions.filter((option) => getOptionSearchText(option).toLowerCase().includes(query));
  }, [sortedOptions, searchQuery, searchable]);

  const hasCategories = useMemo(() => options.some((option) => Boolean(option.category)), [options]);

  // Keep the label shrunk when an empty value is still rendered (displayEmpty / "Select"
  // placeholder) or when helper text sits under the field — otherwise the floating label
  // overlaps the value and helper text.
  const shrinkLabel = useMemo(
    () =>
      Boolean(selectProps.displayEmpty) ||
      Boolean(helperText) ||
      options.some((option) => option.value === '' || option.value == null) ||
      undefined,
    [selectProps.displayEmpty, helperText, options]
  );

  const groupedOptions = useMemo(() => {
    if (!hasCategories) return null;

    const groups = new Map<string, Option[]>();
    filteredOptions.forEach((option) => {
      const category = option.category || 'Other';
      const existing = groups.get(category);
      if (existing) {
        existing.push(option);
      } else {
        groups.set(category, [option]);
      }
    });

    return Array.from(groups.entries()).map(([category, categoryOptions]) => ({
      category,
      options: categoryOptions
    }));
  }, [filteredOptions, hasCategories]);

  const handlePlayAudio = (audioUrl?: string, optionValue?: string | number) => {
    if (!audioUrl || !optionValue) return;

    if (playingAudio === optionValue) {
      const audio = audioRefs.current[optionValue];
      if (audio) audio.pause();
      setPlayingAudio(null);
      return;
    }

    Object.values(audioRefs.current).forEach((audio) => {
      if (audio && !audio.paused) audio.pause();
    });

    const audio = audioRefs.current[optionValue];
    if (audio) {
      audio.currentTime = 0;
      audio.play();
      setPlayingAudio(optionValue);
      audio.onended = () => setPlayingAudio(null);
    }
  };

  const CustomArrowIcon = (props: any) => {
    return <IoChevronDownSharp className={props.className} style={{ marginRight: '8px' }} />;
  };

  const focusSearchInput = useCallback(() => {
    requestAnimationFrame(() => {
      searchWrapperRef.current?.querySelector('input')?.focus();
    });
  }, []);

  const handleSearchMenuOpen = useCallback(() => {
    setMenuOpen(true);
    const width = formControlRef.current?.getBoundingClientRect().width;
    if (width) setMenuMinWidth(width);
    focusSearchInput();
  }, [focusSearchInput]);

  const handleSearchMenuClose = useCallback(
    (event: React.SyntheticEvent) => {
      setMenuOpen(false);
      setSearchQuery('');
      selectProps.onClose?.(event);
      selectProps.MenuProps?.onClose?.(event, 'backdropClick');
    },
    [selectProps]
  );

  return (
    <FormControl ref={formControlRef} fullWidth size={selectProps.size ?? size}>
      <InputLabel shrink={shrinkLabel} sx={{ display: 'flex', gap: 0.5 }} id={`${name}-label`}>
        {label} <Typography color='#DA0808'> {selectProps.required && '*'} </Typography>
      </InputLabel>
      <Controller
        name={name}
        control={control}
        rules={rules}
        render={({ field, fieldState: { error } }) => {
          const currentMultiValue = Array.isArray(field.value) ? field.value : [];
          const isImmutable = (value: string | number) =>
            immutableValues.some((immutable) => isOptionValueEqual(immutable, value));
          const isValueSelected = (value: string | number) =>
            currentMultiValue.some((v) => isOptionValueEqual(v, value));
          const atSelectionLimit = isMultiple && selectionLimit != null && currentMultiValue.length >= selectionLimit;

          const applyMultiSelectChange = (nextValue: (string | number)[]) => {
            let values = nextValue.filter((v) => !isOptionValueEqual(v, SELECT_ALL_VALUE));

            immutableValues.forEach((immutable) => {
              if (!values.some((v) => isOptionValueEqual(v, immutable))) {
                values.push(immutable);
              }
            });

            if (selectionLimit != null && values.length > selectionLimit) {
              const immutableInValue = values.filter((v) => isImmutable(v));
              const mutableValues = values.filter((v) => !isImmutable(v));
              const allowedMutable = Math.max(0, selectionLimit - immutableInValue.length);
              values = [...immutableInValue, ...mutableValues.slice(0, allowedMutable)];
            }

            field.onChange(values);
          };

          const toggleMultiOption = (optionValue: string | number, checked: boolean) => {
            if (checked) {
              if (atSelectionLimit && !isValueSelected(optionValue)) return;
              applyMultiSelectChange([...currentMultiValue, optionValue]);
              return;
            }
            if (isImmutable(optionValue)) return;
            applyMultiSelectChange(currentMultiValue.filter((v) => !isOptionValueEqual(v, optionValue)));
          };

          const selectableForAll = filteredOptions.filter((option) => !option.disabled);
          const allVisibleSelected =
            selectableForAll.length > 0 && selectableForAll.every((option) => isValueSelected(option.value));
          const someVisibleSelected = selectableForAll.some((option) => isValueSelected(option.value));

          const toggleSelectAll = () => {
            if (allVisibleSelected) {
              const visibleValues = new Set(selectableForAll.map((option) => String(option.value)));
              applyMultiSelectChange(currentMultiValue.filter((v) => isImmutable(v) || !visibleValues.has(String(v))));
              return;
            }

            const next = [...currentMultiValue];
            selectableForAll.forEach((option) => {
              if (!next.some((v) => isOptionValueEqual(v, option.value))) {
                next.push(option.value);
              }
            });
            applyMultiSelectChange(next);
          };

          return (
            <>
              <SelectComponent
                {...field}
                labelId={`${name}-label`}
                label={label}
                error={!!error}
                {...selectProps}
                {...(searchable
                  ? {
                      open: menuOpen,
                      onOpen: handleSearchMenuOpen,
                      onClose: handleSearchMenuClose
                    }
                  : {})}
                multiple={isMultiple}
                size={size}
                onChange={(event, child) => {
                  if (customOnChange) {
                    customOnChange(event, child);
                    return;
                  }
                  const newValue = event.target.value;
                  if (!isMultiple) {
                    field.onChange(newValue);
                    if (searchable) setMenuOpen(false);
                    return;
                  }
                  const nextValues =
                    typeof newValue === 'string' ? newValue.split(',') : [...(newValue as (string | number)[])];
                  if (nextValues.some((v) => isOptionValueEqual(v, SELECT_ALL_VALUE))) {
                    toggleSelectAll();
                    return;
                  }
                  applyMultiSelectChange(nextValues);
                }}
                MenuProps={
                  searchable
                    ? {
                        ...selectProps.MenuProps,
                        autoFocus: false,
                        disableAutoFocusItem: true,
                        disableEnforceFocus: true,
                        MenuListProps: {
                          ...selectProps.MenuProps?.MenuListProps,
                          sx: {
                            overflow: 'visible',
                            paddingTop: 0,
                            ...selectProps.MenuProps?.MenuListProps?.sx
                          }
                        },
                        PaperProps: {
                          ...selectProps.MenuProps?.PaperProps,
                          onScroll: (event: React.UIEvent<HTMLDivElement>) => {
                            handleScroll(event as React.UIEvent<HTMLElement>);
                            const paperOnScroll = selectProps.MenuProps?.PaperProps?.onScroll;
                            if (typeof paperOnScroll === 'function') {
                              paperOnScroll(event);
                            }
                          },
                          sx: {
                            maxHeight: 250,
                            overflowY: 'auto',
                            overflowX: 'hidden',
                            scrollbarGutter: 'stable',
                            boxSizing: 'border-box',
                            width: 'max-content',
                            ...(menuMinWidth != null && { minWidth: menuMinWidth }),
                            maxWidth: 'calc(100vw - 48px)',
                            ...selectProps.MenuProps?.PaperProps?.sx
                          }
                        }
                      }
                    : {
                        ...selectProps.MenuProps,
                        PaperProps: {
                          ...selectProps.MenuProps?.PaperProps,
                          onScroll: (event: React.UIEvent<HTMLDivElement>) => {
                            handleScroll(event as React.UIEvent<HTMLElement>);
                            const paperOnScroll = selectProps.MenuProps?.PaperProps?.onScroll;
                            if (typeof paperOnScroll === 'function') {
                              paperOnScroll(event);
                            }
                          },
                          style: {
                            maxHeight: 250,
                            ...selectProps.MenuProps?.PaperProps?.style
                          }
                        }
                      }
                }
                sx={{
                  '.MuiOutlinedInput-notchedOutline': {
                    borderColor: '#ececec'
                  },
                  ...selectProps.sx
                }}
                IconComponent={CustomArrowIcon}
                renderValue={(selected) => {
                  if (!Array.isArray(selected)) {
                    const selectedOption = options.find((option) => {
                      return String(option.value) === String(selected);
                    });
                    return (
                      <Typography noWrap>
                        <>{selectedOption?.label || selected}</>
                      </Typography>
                    );
                  }

                  const visibleOptions = selected.slice(0, visibleCount);
                  const hiddenOptions = selected.slice(visibleCount);

                  return (
                    <Box display='flex' flexWrap='wrap' gap={0.5}>
                      {visibleOptions.map((value) => {
                        const option = options.find((o) => isOptionValueEqual(o.value, value));
                        if (!option) return null;
                        const chipImmutable = isImmutable(value);

                        return (
                          <Chip
                            key={option.value}
                            label={option.label}
                            sx={{
                              background: theme.palette.primary.light,
                              color: theme.palette.primary.main,
                              borderRadius: '4px',
                              textTransform: 'none'
                            }}
                            size='small'
                            deleteIcon={
                              chipImmutable ? undefined : <RxCross2 size={14} color={theme.palette.primary.main} />
                            }
                            onMouseDown={(event) => event.stopPropagation()}
                            onDelete={
                              chipImmutable
                                ? undefined
                                : (event) => {
                                    event.stopPropagation();
                                    applyMultiSelectChange(selected.filter((v) => !isOptionValueEqual(v, value)));
                                  }
                            }
                          />
                        );
                      })}

                      {hiddenOptions.length > 0 && (
                        <Tooltip
                          arrow
                          placement='top'
                          title={hiddenOptions
                            .map((value) => options.find((o) => o.value === value)?.label)
                            .filter(Boolean)
                            .join(', ')}
                        >
                          {visibleCount === 0 ? (
                            <Box
                              sx={{
                                fontSize: 15,
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center'
                              }}
                            >
                              {hiddenOptions.length} Selected
                            </Box>
                          ) : (
                            <Box
                              sx={{
                                background: theme.palette.primary.light,
                                color: theme.palette.primary.main,
                                borderRadius: '4px',
                                px: 1,
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center'
                              }}
                            >
                              +{hiddenOptions.length}
                            </Box>
                          )}
                        </Tooltip>
                      )}
                    </Box>
                  );
                }}
                value={isMultiple ? (Array.isArray(field.value) ? field.value : []) : (field.value ?? '')}
              >
                {searchable && (
                  <ListSubheader
                    onMouseDown={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      focusSearchInput();
                    }}
                    sx={{
                      position: 'sticky',
                      top: 0,
                      zIndex: 2,
                      bgcolor: 'background.paper',
                      // borderBottom: '1px solid #ececec',
                      lineHeight: 'normal',
                      width: '100%',
                      boxSizing: 'border-box',
                      flexShrink: 0,
                      py: 0,
                      pt: 1,
                      pb: 1,
                      px: 1.5
                    }}
                  >
                    <Box ref={searchWrapperRef} sx={{ width: '100%' }}>
                      <Search
                        key={menuOpen ? 'select-search-open' : 'select-search-closed'}
                        searchText={searchQuery}
                        setSearchText={(value) => setSearchQuery(value ?? '')}
                        debounceTime={0}
                        size='small'
                        fullWidth
                        placeholder={searchPlaceholder}
                        onKeyDown={(e) => {
                          if (e.key !== 'Escape') {
                            e.stopPropagation();
                          }
                        }}
                      />
                    </Box>
                  </ListSubheader>
                )}
                {handleAddNewOption && (
                  <MenuItem
                    value=''
                    onClick={(e) => {
                      e.stopPropagation();
                      handleAddNewOption();
                    }}
                  >
                    <Box
                      color='secondary'
                      display='flex'
                      justifyContent='center'
                      alignItems='center'
                      py={0.8}
                      width='100%'
                      borderRadius='4px'
                      border='1px solid #F3F3F3'
                    >
                      <BiPlus />
                      <Typography>Add new option</Typography>
                    </Box>
                  </MenuItem>
                )}
                {searchable && searchQuery.trim() && filteredOptions.length === 0 && (
                  <MenuItem disabled>
                    <Typography color='text.secondary'>No results found</Typography>
                  </MenuItem>
                )}
                {isMultiple && allSelect && !searchQuery.trim() && selectableForAll.length > 0 && (
                  <MenuItem
                    value={SELECT_ALL_VALUE}
                    sx={searchable ? { whiteSpace: 'nowrap', width: 'max-content', minWidth: '100%' } : undefined}
                  >
                    <Box display='flex' gap={0.5} alignItems='center'>
                      <Checkbox
                        checked={allVisibleSelected}
                        indeterminate={!allVisibleSelected && someVisibleSelected}
                        onClick={(e) => e.stopPropagation()}
                        onChange={() => {
                          toggleSelectAll();
                        }}
                      />
                      <Typography sx={searchable ? { whiteSpace: 'nowrap' } : undefined}>All</Typography>
                    </Box>
                  </MenuItem>
                )}
                {searchable &&
                  !isMultiple &&
                  field.value != null &&
                  field.value !== '' &&
                  !filteredOptions.some((option) => isOptionValueEqual(option.value, field.value)) && (
                    <MenuItem value={field.value} sx={{ display: 'none' }} />
                  )}
                {groupedOptions
                  ? groupedOptions.flatMap(({ category, options: categoryOptions }) => [
                      // eslint-disable-next-line react/jsx-indent
                      <ListSubheader
                        key={`category-${category}`}
                        onClick={(event) => event.stopPropagation()}
                        sx={{
                          lineHeight: '32px',
                          color: 'text.secondary',
                          fontSize: 12,
                          fontWeight: 600,
                          textTransform: 'uppercase',
                          letterSpacing: '0.04em',
                          bgcolor: 'background.paper'
                        }}
                      >
                        {category}
                      </ListSubheader>,
                      ...categoryOptions.map((option) => {
                        const isSelected = isMultiple
                          ? isValueSelected(option.value)
                          : isOptionValueEqual(field.value ?? '', option.value);
                        const optionImmutable = isImmutable(option.value);
                        const optionDisabled =
                          option.disabled ||
                          (isMultiple && optionImmutable && isSelected) ||
                          (isMultiple && !isSelected && atSelectionLimit);
                        return (
                          <MenuItem
                            key={option.value}
                            value={option.value}
                            disabled={optionDisabled}
                            selected={option.selected || false}
                            sx={
                              searchable ? { whiteSpace: 'nowrap', width: 'max-content', minWidth: '100%' } : undefined
                            }
                          >
                            <Box
                              display='flex'
                              justifyContent='space-between'
                              alignItems='center'
                              width={searchable ? 'max-content' : '100%'}
                            >
                              <Box display='flex' gap={0.5} alignItems='center'>
                                {isMultiple && (
                                  <Checkbox
                                    checked={isSelected}
                                    disabled={optionDisabled}
                                    onClick={(e) => e.stopPropagation()}
                                    onChange={(e) => {
                                      toggleMultiOption(option.value, e.target.checked);
                                    }}
                                  />
                                )}
                                <Typography sx={searchable ? { whiteSpace: 'nowrap' } : undefined}>
                                  {option.label}
                                </Typography>
                              </Box>
                              {option.audioUrl && (
                                <>
                                  {playingAudio === option.value ? (
                                    <MdStopCircle
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handlePlayAudio(option.audioUrl, option.value);
                                      }}
                                      style={{ cursor: 'pointer' }}
                                    />
                                  ) : (
                                    <FaRegCirclePlay
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handlePlayAudio(option.audioUrl, option.value);
                                      }}
                                      style={{ cursor: 'pointer' }}
                                    />
                                  )}
                                  <audio ref={(el) => (audioRefs.current[option.value] = el)} src={option.audioUrl} />
                                </>
                              )}
                            </Box>
                          </MenuItem>
                        );
                      })
                    ])
                  : filteredOptions.map((option) => {
                      const isSelected = isMultiple
                        ? isValueSelected(option.value)
                        : isOptionValueEqual(field.value ?? '', option.value);
                      const optionImmutable = isImmutable(option.value);
                      const optionDisabled =
                        option.disabled ||
                        (isMultiple && optionImmutable && isSelected) ||
                        (isMultiple && !isSelected && atSelectionLimit);
                      return (
                        <MenuItem
                          key={option.value}
                          value={option.value}
                          disabled={optionDisabled}
                          selected={option.selected || false}
                          sx={searchable ? { whiteSpace: 'nowrap', width: 'max-content', minWidth: '100%' } : undefined}
                        >
                          <Box
                            display='flex'
                            justifyContent='space-between'
                            alignItems='center'
                            width={searchable ? 'max-content' : '100%'}
                          >
                            <Box display='flex' gap={0.5} alignItems='center'>
                              {isMultiple && (
                                <Checkbox
                                  checked={isSelected}
                                  disabled={optionDisabled}
                                  onClick={(e) => e.stopPropagation()} // Prevent MenuItem click trigger
                                  onChange={(e) => {
                                    toggleMultiOption(option.value, e.target.checked);
                                  }}
                                />
                              )}
                              <Typography sx={searchable ? { whiteSpace: 'nowrap' } : undefined}>
                                {option.label}
                              </Typography>
                            </Box>
                            {option.audioUrl && (
                              <>
                                {playingAudio === option.value ? (
                                  <MdStopCircle
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handlePlayAudio(option.audioUrl, option.value);
                                    }}
                                    style={{ cursor: 'pointer' }}
                                  />
                                ) : (
                                  <FaRegCirclePlay
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handlePlayAudio(option.audioUrl, option.value);
                                    }}
                                    style={{ cursor: 'pointer' }}
                                  />
                                )}
                                <audio ref={(el) => (audioRefs.current[option.value] = el)} src={option.audioUrl} />
                              </>
                            )}
                          </Box>
                        </MenuItem>
                      );
                    })}
                {isLoading && (
                  <MenuItem disabled>
                    <Box display='flex' justifyContent='center' alignItems='center' width='100%'>
                      <Typography>Loading...</Typography>
                    </Box>
                  </MenuItem>
                )}
              </SelectComponent>
              <FormHelperText sx={{ color: error ? theme.palette.error.main : undefined }}>
                {error ? error.message : helperText}
              </FormHelperText>
            </>
          );
        }}
      />
    </FormControl>
  );
}
